import { HubRichTextBackground, HubHeroBackground, Maybe, Slices } from '@/__generated__/graphql';
import { colors } from '@theme/lib';

export enum PrimaryButtonType {
  HoneyBee = 'honey_bee',
  Seagrass = 'seagrass',
}

export enum SecondaryButtonType {
  Seagrass = 'seagrass',
  SeagrassBlack ='seagrass_black',
}

export type ButtonProps = {
  text: string;
  bg: string;
  border: string;
  focusBorder: string;
  hoverBorder: string;
  hoverBackground: string;
  hoverText: string;
};

export const getPrimaryButtonProperties = (bgColor: string, containerBackground?: string) => {
  switch (bgColor) {
    case PrimaryButtonType.HoneyBee:
      return {
        text: colors.black,
        bg: colors.honeyBee,
        border: colors.honeyBee,
        focusBorder: colors.honeyBee,
        hoverBorder: colors.honeyBee,
        hoverBackground: colors.white,
        hoverText: colors.black,
      };
    case PrimaryButtonType.Seagrass:
      if (containerBackground && containerBackground == "none") {
        return {
          text: colors.white,
          bg: colors.seagrass,
          border: colors.seagrass,
          focusBorder: colors.gecko,
          hoverBorder: colors.gecko,
          hoverBackground: colors.white,
          hoverText: colors.black,
        };
      }
      return {
        text: colors.white,
        bg: colors.seagrass,
        border: colors.seagrass,
        focusBorder: colors.honeyBee,
        hoverBorder: colors.honeyBee,
        hoverBackground: colors.white,
        hoverText: colors.black,
      };
  }

  return {
    text: colors.black,
    bg: colors.honeyBee,
    border: colors.honeyBee,
    focusBorder: colors.honeyBee,
    hoverBorder: colors.honeyBee,
    hoverBackground: colors.white,
    hoverText: colors.black,
  };
};

type BackgroundStyleProperties = {
  backgroundColor?: string;
  headingColor?: string;
  textColor?: string;
  backgroundImage?: string;
  backgroundCurve?: string;
  button: {
    primary: ButtonProps;
    secondary: ButtonProps;
  };
};

// Function to get style properties based on background prop
export const getBackgroundPatternProperties = (
  backgroundProp?: Maybe<HubRichTextBackground | string>
): BackgroundStyleProperties => {
  switch (backgroundProp) {
    case HubRichTextBackground.honey_bee:
      return {
        backgroundColor: colors.honeyBee,
        headingColor: colors.seagrass,
        textColor: colors.black,
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.Seagrass),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.Seagrass, HubRichTextBackground.honey_bee)
        },
      };

    case HubRichTextBackground.honey_bee_with_texture:
      return {
        backgroundColor: colors.honeyBee,
        headingColor: colors.seagrass,
        textColor: colors.black,
        backgroundImage: 'ZSL200_Texture_HoneyBee_RGB.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.Seagrass),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.Seagrass, HubRichTextBackground.honey_bee)
        },
      };

    case HubRichTextBackground.seagrass:
      return {
        backgroundColor: colors.seagrass,
        headingColor: colors.honeyBee,
        textColor: colors.white,
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.HoneyBee),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.Seagrass,  HubRichTextBackground.seagrass)
        },
      };

    case HubRichTextBackground.seagrass_with_texture:
      return {
        backgroundColor: colors.seagrass,
        headingColor: colors.honeyBee,
        textColor: colors.white,
        backgroundImage: 'ZSL200_Texture_Seagrass_RGB.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.HoneyBee),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.Seagrass,  HubRichTextBackground.seagrass)
        },
      };

    default:
      return {
        backgroundColor: colors.whiteLight,
        headingColor: colors.butterfly,
        textColor: colors.black,
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.Seagrass, "none"),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.Seagrass, "none")
        },
      };
  }
};

export const getSecondaryButtonProperties = (buttonVariant: string, containerBackground?: string) => {
  switch (buttonVariant) {
    case SecondaryButtonType.Seagrass:
      if (containerBackground && containerBackground == "none") {
        return {
          text: colors.seagrass,
          bg: colors.white,
          border: colors.seagrass,
          focusBorder: colors.seagrass,
          hoverBorder: colors.seagrass,
          hoverBackground: colors.honeyBee,
          hoverText: colors.black,
        };
      }
      return {
        text: colors.seagrass,
        bg: colors.white,
        border: colors.seagrass,
        focusBorder: colors.seagrass,
        hoverBorder: colors.seagrass,
        hoverBackground: colors.honeyBee,
        hoverText: colors.black,
      };
    case SecondaryButtonType.SeagrassBlack:
      return {
       text: colors.seagrass,
        bg: colors.white,
        border: colors.black,
        focusBorder: colors.black,
        hoverBorder: colors.black,
        hoverBackground: colors.honeyBee,
        hoverText: colors.black,
      };
  }

  return {
    text: colors.seagrass,
    bg: colors.white,
    border: colors.seagrass,
    focusBorder: colors.seagrass,
    hoverBorder: colors.seagrass,
    hoverBackground: colors.honeyBee,
    hoverText: colors.black,
  };
};

export const getHeroBackgroundPatternProperties = (
  backgroundProp?: Maybe<HubHeroBackground>
): BackgroundStyleProperties => {
  switch (backgroundProp) {
    case HubHeroBackground.honey_bee:
      return {
        backgroundColor: colors.honeyBee,
        headingColor: colors.seagrass,
        textColor: colors.black,
        backgroundImage: 'ZSL200_Texture_HoneyBee_RGB.svg',
        backgroundCurve: '200_HeroCurve_HoneyBee.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.Seagrass),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.Seagrass),
        }
      };

    case HubHeroBackground.sky:
      return {
        backgroundColor: colors.sky,
        headingColor: colors.seagrass,
        textColor: colors.black,
        backgroundImage: 'ZSL200_Texture_Sky_RGB.svg',
        backgroundCurve: '200_HeroCurve_Sky.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.Seagrass),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.SeagrassBlack),
        }
      };

    case HubHeroBackground.seagrass:
      return {
        backgroundColor: colors.seagrass,
        headingColor: colors.honeyBee,
        textColor: colors.white,
        backgroundImage: 'ZSL200_Texture_Seagrass_RGB.svg',
        backgroundCurve: '200_HeroCurve_Seagrass.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.HoneyBee),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.SeagrassBlack),
        }
      };

    case HubHeroBackground.flamingo:
      return {
        backgroundColor: colors.flamingo,
        headingColor: colors.honeyBee,
        textColor: colors.white,
        backgroundImage: 'ZSL200_Texture_Flamingo_RGB.svg',
        backgroundCurve: '200_HeroCurve_Flamingo.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.HoneyBee),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.SeagrassBlack),
        }
      };

    default:
      return {
        backgroundColor: colors.tiger,
        headingColor: colors.butterfly,
        textColor: colors.black,
        backgroundImage: 'ZSL200_Texture_Tiger_RGB.svg',
        backgroundCurve: '200_HeroCurve_Tiger.svg',
        button: {
          primary: getPrimaryButtonProperties(PrimaryButtonType.Seagrass),
          secondary: getSecondaryButtonProperties(SecondaryButtonType.SeagrassBlack),
        }
      };
  }
};

export const hasBicetenaryBackground = (paragraph: Slices): boolean => {
    switch (paragraph.__typename) {
      case "AudioParagraph":
        return false;
      case "RichTextParagraph":
        return !!paragraph.field_hub_rich_text_background;
      case "PromoBlockParagraph":
        return false;
      case "StatisticsParagraph":
        return true;
      case "ProjectInDetailSliderParagraph":
        return false;
      case "FeatureCardParagraph":
        return Boolean(paragraph.backgroundColour && paragraph.backgroundColour !== 'White');
      case "CarouselParagraph":
        return false;
      case "ImagesParagraph":
        return true;
      case "GeneralListingParagraph":
        return false;
      case "ImageGalleryParagraph":
        return true;
      case "FeatureCard2_3Paragraph":
        return true;
      case "EventListingParagraph":
        return false;
      case "TextImageParagraph":
        return true;
      case "TestimonialsParagraph":
        return true;
      default:
        return false;
    }
}

export const bicentenaryBackgroundClass = (paragraph: Slices) => {
  if (hasBicetenaryBackground(paragraph)) {
    return 'background-enabled';
  }
  return '';
}